import {
  AddPracticeVisitArgs,
  AddReceivedPaymentArgs,
  AddUsersToThreadArgs,
  AnyObject,
  AssignThreadFileAsConsentFormArgs,
  BaseQuery,
  CombineFilesIntoPdfArgs,
  ConfirmPaymentInstallmentArgs,
  CopyChatFileToAssessmentArgs,
  CopyChatFileToPatientArgs,
  CreateNewThreadArgs,
  DeleteThreadArgs,
  DeleteThreadMessageArgs,
  EditThreadMessageArgs,
  EditThreadTitleArgs,
  EligibilityResponse,
  EmptyObject,
  FaxVisitPdfArgs,
  FetchWebpageMetadataArgs,
  FetchWebpageMetadataResponse,
  MakeCoveragePrimaryArgs,
  MarkThreadReadStatusArgs,
  MovePatientFileArgs,
  NotificationTopic,
  PlaceBillingHoldArgs,
  ProcessPatientFileUploadArgs,
  RegisterWebPushNotificationTokenArgs,
  RemoveUsersFromThreadArgs,
  ReportDeliveryArgs,
  ReportMessageReadArgs,
  ReportPaymentInstallmentArgs,
  ReportPracticeFileUploadArgs,
  ReportProfilePictureUploadArgs,
  ReportThreadReadArgs,
  RequestCallInArgs,
  RequestCoverageArgs,
  RequestExamPdfArgs,
  RequestPrenatalRecordPdfArgs,
  RequestUltrasoundPdfArgs,
  RequestVisitPdfArgs,
  SearchThreadArgs,
  SendThreadMessageArgs,
  SetPregnancyCoverageLabelArgs,
  SubmitEligibilityRequestArgs,
  ThreadSearchResult,
  ToggleThreadMuteArgs,
  ToggleThreadReactionArgs,
  UpdateAdminColorArgs,
  UpdateSharedNoteArgs,
  UserInviteProps,
  UserInviteResponse,
  WithId,
} from '@hb/shared'
import { HttpsCallable, httpsCallable } from 'firebase/functions'
import { functions } from './functions'

export const manuallyAddPaymentToInvoice = httpsCallable<{
  assessmentId: string
  paymentId: string
}>(functions, 'manuallyAddPaymentToInvoice')
export const manuallyAddPaymentToSpecificInvoice = httpsCallable<{
  assessmentId: string
  paymentId: string
  invoiceId: string
}>(functions, 'manuallyAddPaymentToSpecificInvoice')
export const removeManualPaymentFromInvoice = httpsCallable<{
  assessmentId: string
  paymentId: string
  invoiceId: string
}>(functions, 'removeManualPaymentFromInvoice')
export const assignFaxToAssessment = httpsCallable<{
  faxId: string
  assessmentId: string | null
}>(functions, 'assignFaxToAssessment')

export const createInvoicePreview = httpsCallable<{ invoiceId: string }, string>(
  functions,
  'createInvoicePreview',
)
export const addReceivedPayment = httpsCallable<AddReceivedPaymentArgs>(
  functions,
  'addReceivedPayment',
)

export const deleteReceivedPayment = httpsCallable<{
  assessmentId: string
  paymentId: string
}>(functions, 'deleteReceivedPayment')

export const updateAssessmentMiscarriedStatus = httpsCallable<{
  assessmentId: string
  miscarried: boolean
}>(functions, 'updateAssessmentMiscarriedStatus')

export const updateAssessmentDeliveryStatus = httpsCallable<ReportDeliveryArgs>(
  functions,
  'updateAssessmentDeliveryStatus',
)

export const sendMessageFunction = httpsCallable<SendThreadMessageArgs, string>(
  functions,
  'sendThreadMessage',
)

export const deleteMessageFunction = httpsCallable<DeleteThreadMessageArgs, { success: string }>(
  functions,
  'deleteThreadMessage',
)

export const editMessageFunction = httpsCallable<EditThreadMessageArgs, { success: string }>(
  functions,
  'editThreadMessage',
)

export const reportMessageReadFunction = httpsCallable<ReportMessageReadArgs, { success: string }>(
  functions,
  'reportMessageRead',
)

export const reportThreadReadFunction = httpsCallable<ReportThreadReadArgs, { success: string }>(
  functions,
  'reportThreadRead',
)

export const markThreadReadStatusFunction = httpsCallable<
  MarkThreadReadStatusArgs,
  { success: string }
>(functions, 'markThreadReadStatus')

export const toggleReactionFunction = httpsCallable<
  ToggleThreadReactionArgs,
  { success: string; action: 'added' | 'removed' }
>(functions, 'toggleThreadReaction')

export const toggleThreadMuteFunction = httpsCallable<ToggleThreadMuteArgs, { success: string }>(
  functions,
  'toggleThreadMute',
)

export const createNewThreadFunction = httpsCallable<CreateNewThreadArgs>(
  functions,
  'createNewThread',
)
export const deleteThreadFunction = httpsCallable<DeleteThreadArgs>(functions, 'deleteThread')

export const updateAdminColorFunction = httpsCallable<UpdateAdminColorArgs>(
  functions,
  'updateAdminColor',
)

export const addUsersToThread = httpsCallable<AddUsersToThreadArgs>(functions, 'addUsersToThread')
export const removeUsersFromThread = httpsCallable<RemoveUsersFromThreadArgs>(
  functions,
  'removeUsersFromThread',
)
export const editThreadTitleFunction = httpsCallable<EditThreadTitleArgs, { success: string }>(
  functions,
  'editThreadTitle',
)
export const reportProfilePictureUpload = httpsCallable<ReportProfilePictureUploadArgs>(
  functions,
  'reportProfilePictureUpload',
)

export const searchThread = httpsCallable<SearchThreadArgs, ThreadSearchResult[]>(
  functions,
  'searchThread',
)

export const searchThreadsFunction = httpsCallable<{ query: string }, ThreadSearchResult[]>(
  functions,
  'searchMyThreads',
)

export const searchMyThreads = async (query: string) => {
  const { data } = await searchThreadsFunction({ query })
  return data
}

export const copyChatFileToPatient = httpsCallable<CopyChatFileToPatientArgs>(
  functions,
  'copyChatFileToPatient',
)

export const copyChatFileToAssessment = httpsCallable<CopyChatFileToAssessmentArgs>(
  functions,
  'copyChatFileToAssessment',
)

export const movePatientFile = httpsCallable<
  MovePatientFileArgs,
  { success: boolean; newFileId: string }
>(functions, 'movePatientFile')

export const processPatientFileUpload = httpsCallable<
  ProcessPatientFileUploadArgs,
  { success: boolean }
>(functions, 'processPatientFileUpload')

export const assignThreadFileAsConsentForm = httpsCallable<AssignThreadFileAsConsentFormArgs>(
  functions,
  'assignThreadFileAsConsentForm',
)

// authentication
export const removeSecondaryFactor = httpsCallable<{ factorId: string }>(
  functions,
  'removeSecondaryFactor',
)
export const renameSecondaryFactor = httpsCallable<{ factorId: string; displayName: string }>(
  functions,
  'renameSecondaryFactor',
)
export const generatePDF = httpsCallable<{ html: string }, { path: string }>(
  functions,
  'generatePDF',
)

export const reportPracticeFileUpload = httpsCallable<ReportPracticeFileUploadArgs>(
  functions,
  'reportPracticeFileUpload',
)

export const fetchWebpageMetadata = httpsCallable<
  FetchWebpageMetadataArgs,
  FetchWebpageMetadataResponse
>(functions, 'fetchWebpageMetadata')

export const registerWebPushNotificationToken = httpsCallable<RegisterWebPushNotificationTokenArgs>(
  functions,
  'registerWebPushNotificationToken',
)
export const unregisterWebPushNotificationToken = httpsCallable<EmptyObject>(
  functions,
  'unregisterWebPushNotificationToken',
)

export const updateWebPushNotificationSettings = httpsCallable<{
  topic: NotificationTopic
  enabled: boolean
}>(functions, 'updateWebPushNotificationSettings')

export const reportPaymentInstallment = httpsCallable<ReportPaymentInstallmentArgs>(
  functions,
  'reportPaymentInstallment',
)
export const confirmPaymentInstallment = httpsCallable<ConfirmPaymentInstallmentArgs>(
  functions,
  'confirmPaymentInstallment',
)
export const combineFilesIntoPdf = httpsCallable<CombineFilesIntoPdfArgs>(
  functions,
  'combineFilesIntoPdf',
)

export const addPracticeVisit = httpsCallable<
  AddPracticeVisitArgs,
  { success: string; id: string; error?: never } | { error: string; id?: never; success?: never }
>(functions, 'addPracticeVisit')
export const requestExamPdf = httpsCallable<RequestExamPdfArgs, { storagePath: string }>(
  functions,
  'requestExamPdf',
)
export const requestUltrasoundPdf = httpsCallable<
  RequestUltrasoundPdfArgs,
  { storagePath: string }
>(functions, 'requestUltrasoundPdf')
export const requestPrenatalRecordPdf = httpsCallable<
  RequestPrenatalRecordPdfArgs,
  { storagePath: string }
>(functions, 'requestPrenatalRecordPdf')
export const requestVisitPdf = httpsCallable<
  RequestVisitPdfArgs,
  { success: boolean; storagePath: string }
>(functions, 'requestVisitPdf')

export const fixAssessmentFiles = httpsCallable<
  { assessmentId: string },
  { numFixedFiles: number; numFixedValues: number }
>(functions, 'fixAssessmentFiles')
export const fixAssessmentInvoiceSnippets = httpsCallable<{ assessmentId: string }>(
  functions,
  'fixAssessmentInvoiceSnippets',
)

export const requestCoverage = httpsCallable<RequestCoverageArgs, { success: boolean }>(
  functions,
  'requestCoverage',
)
export const makeCoveragePrimary = httpsCallable<MakeCoveragePrimaryArgs, { success: string }>(
  functions,
  'makeCoveragePrimary',
)

export const setPregnancyCoverageLabel = httpsCallable<
  SetPregnancyCoverageLabelArgs,
  { success: string }
>(functions, 'setPregnancyCoverageLabel')

export const requestCallIn = httpsCallable<RequestCallInArgs, { success: string }>(
  functions,
  'requestCallIn',
)
export const sendCallInRequestEmail = httpsCallable<{
  coverageId: string
  assessmentId: string
  message: string
}>(functions, 'sendCallInRequestEmail')
export const updateSharedNote = httpsCallable<UpdateSharedNoteArgs>(functions, 'updateSharedNote')

export const faxVisitPdf = httpsCallable<FaxVisitPdfArgs, { success: string; storagePath: string }>(
  functions,
  'faxVisitPdf',
)

export const placeBillingHold = httpsCallable<PlaceBillingHoldArgs, { success: boolean }>(
  functions,
  'placeBillingHold',
)

export const submitEligibilityRequest = httpsCallable<
  SubmitEligibilityRequestArgs,
  EligibilityResponse
>(functions, 'submitEligibilityRequest')

export const inviteUserFunction = httpsCallable<Omit<UserInviteProps, 'id'>, UserInviteResponse>(
  functions,
  'inviteUser',
)
export const baseGetQueryCount = httpsCallable<BaseQuery<AnyObject>, number>(
  functions,
  'getQueryCount',
)

export const getQueryCount = <T extends AnyObject>(query: BaseQuery<T>) =>
  baseGetQueryCount(query as BaseQuery<AnyObject>).then(d => d.data)

export const baseFetchQuery = httpsCallable<BaseQuery<AnyObject>, AnyObject>(
  functions,
  'fetchQuery',
)

export const fetchQuery = <T extends AnyObject>(query: BaseQuery<T>) =>
  (baseFetchQuery as HttpsCallable<BaseQuery<T>, WithId<T>[]>)(query)

export const deleteAccountFunction = httpsCallable<AnyObject, { success: boolean }>(
  functions,
  'deleteAccount',
)
